import { useState, useEffect, useMemo } from 'react';
import { useLocation, useHistory } from "react-router-dom";
import {
  DataGridPremium,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from '@mui/x-data-grid-premium';
import { useGetBuyingEmailPagedQuery } from '../../services/buyingEmailApi';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Checkbox, MenuItem, Modal, Select } from '@mui/material';
import BuyingEmailForm from './components/BuyingEmailForm';
import { useDispatch } from 'react-redux';
import Stack from '@mui/material/Stack';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CloseIcon from '@mui/icons-material/Close';

import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import EditUser from "./components/accounts/EditUser";
import Notification from '../../components/Notification';

import DebounceInput from '../../components/DebounceInput';
import { updateAccount } from '../../store/slices/accounts/accounts';
import { apiSlice } from '../../app/api/apiSlice';
import { useNotification } from '../../context/NotificationContext';

const DEFAULT_HIDE_COLUMNS = {
  columnVisibilityModel: {
    // Hide columns status and traderName, the other columns will remain visible
    id: false,
  },
}

function getDefaultsValues(searchParams) {  
  return {
    pageNumber: Number(searchParams.get("pageNumber")) || 1,
    pageSize: Number(searchParams.get("pageSize")) || 100,
    emailStatusFilter: searchParams.get("emailStatusFilter") || "ACTIVE_ONLY",
    search: searchParams.get("search") || "",
  }
}

/*A: 
* this useEffect is to add a new field to the buyingEmails object, this field is called "assignedUserName" and is a combination of the assignedUserName and the id
* this is because we need to render actions buttons for group header and use functions that need the id of the user, but we can not get this id in params.row
* because params.row is empty when we are in the group header, so we added the id in the assignedUserName field, then material UI use this field to create a custom id
* so we can get the id of the autogenerated id: "auto-generated-row-assignedUserName/Sheyenne Tormey-8" -> "Sheyenne Tormey-8" -> "8"
*/
function buyingEmailToDataGridFormat(buyingEmail, userInfo) {
  const assignedUserIsDeleted = userInfo?.find(x => x.userId === buyingEmail.assignedUserID)?.assignedUserIsDeleted || false;
  let name = buyingEmail.assignedUserName + "-" + buyingEmail.assignedUserID + "-" + assignedUserIsDeleted;

  return {
    id: buyingEmail.id,
    assignedUserName: name,
    emailAddress: buyingEmail.emailAddress,
    isActive: buyingEmail.isActive,
    isDeleted: buyingEmail.isDeleted
  }
}

function getBuyingEmailAsArray(apiResponse) {
  let buyingEmailAsArray = ((apiResponse?.items.map(item => item.emails) || [])).flat();
  return buyingEmailAsArray.map((item) => buyingEmailToDataGridFormat(item, apiResponse?.items));
}

const emailStatusFilterOptions = {
  ACTIVE_ONLY: true,
  INACTIVE_ONLY: false,
  ALL: null
}

export default function RowGroupingBasicExample() {
  const location = useLocation();
  const history = useHistory();

  const searchParams = new URLSearchParams(location.search);

  const [userIdOnEditing, setUserIdOnEditing] = useState(null);
  const [openAddEmailModal, setOpenAddEmailModal] = useState(null);
  
  const { showNotification } = useNotification();

  const { pageNumber: pageNumberDflt, pageSize: pageSizeDflt, emailStatusFilter: emailStatusFilterDftl, search: searchDflt } = getDefaultsValues(searchParams);
  const [emailStatusFilter, setEmailStatusFilter] = useState(emailStatusFilterDftl); // by default we show only active emails
  const [pageNumber, setPageNumber] = useState(pageNumberDflt);
  const [pageSize, setPageSize] = useState(pageSizeDflt);
  const [search, setSearch] = useState(searchDflt);

  // ------------------- crear columnas del DataGrid -------------------
  const [columns, setColumns] = useState([
    { field: "id", headerName: "ID" },
    { field: "assignedUserName", headerName: "ASSIGNED USER'S ACCOUNT", width: 250, renderCell: (params) => { return getColumnGroupRender(params) } },
    { field: "emailAddress", headerName: "BUYING EMAILS", flex: 1.5 },
    { field: "isActive", headerName: "ACTIVE", flex: 0.5, justifyContent: "center", renderCell: getIsActiveIcon, sortable: false },
    { field: "isDeleted", headerName: "DELETED", flex: 0.5, renderCell: getIsDeletedIcon, sortable: false },
    { field: "actions", headerName: "ACTIONS", flex: 2, renderCell: (params) => { return getActionsButtons(params) }, sortable: false }
  ]);

  const { data: buyingEmailsRes, isLoading: isBuyingEmailsLoading, isFetching: isBuyingEmailFetching, refetch: reFetchBuyingEmails } = useGetBuyingEmailPagedQuery({
    isActive: emailStatusFilterOptions[emailStatusFilter],
    pageNumber,
    pageSize,
    search
  });

  const buyingEmailAsArray = useMemo(() => {
    return getBuyingEmailAsArray(buyingEmailsRes);
  }, [buyingEmailsRes]);

  const apiRef = useGridApiRef();

  function onAddEmailClick(id) {
    setOpenAddEmailModal({ userId: id });
  }

  function onEditEmail(params) {
    console.log(params);
    let assignedUser = params.row.assignedUserName.split("-");
    let userId = assignedUser[1];
    let emailId = params.row.id;
    setOpenAddEmailModal({ userId: userId, emailId: emailId, title: `${assignedUser[0]} Buying Email details` });
  }

  function onEmailFormSuccess(res, message) {
    showNotification(message, 'success');
  }

  function onEmailFormError(res, message) {
    showNotification(message, 'error');
  }

  // ------------------- funciones de columnas -------------------
  
  function getActionsButtons(params) {
    if (Object.keys(params.row).length == 0) { //A: if the row is empty its means that is the title header
      //A: if we are in the title header we should add two actions buttons and call functions, this funcionts need a id to work
      //A: but params.row is empty, so we added the id in the assignedUserName field, then material UI use this field to create a custom id
      //A: so we can get the id of the autogenerated id: "auto-generated-row-assignedUserName/Sheyenne Tormey-8" -> "Sheyenne Tormey-8" -> "8"

      let data = params.id.split("/")[1]; //example: "auto-generated-row-assignedUserName/Sheyenne Tormey-8" -> "Sheyenne Tormey-8"
      //divide the string by "-" and take the last two elements
      let parts = data.split("-");
      let id = parts[parts.length - 2]; // antepenultimate part: "8" -> example: "Sheyenne Tormey-8-true" -> "8"
      let assignedUserIsDeleted = parts[parts.length - 1]; // last part: "true" -> example: "Sheyenne Tormey-8-true" -> "true"
      
      return <div>
        <Stack
          direction="row"
          spacing={{}}
        >
          <Button onClick={() => intManageAccount(id, assignedUserIsDeleted)}> <ManageAccountsOutlinedIcon sx={{ margin: "0 0.25em 0 0" }} /> Manage Account</Button>
          <Button onClick={() => onAddEmailClick(id)}> <AddOutlinedIcon sx={{ margin: "0 0.25em 0 0" }} /> Add Email</Button>
        </Stack>
      </div>
    } else {
      return (
        <div className="d-flex align-items-center">
          <div>
            <Button onClick={() => onEditEmail(params)}>Edit</Button>
          </div>
        </div>
      );
    }
  }

  function getColumnGroupRender(params) {
    if (
      params.rowNode.type === 'group' &&
      params.field === params.rowNode.groupingField
    ) {
      return '';
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ marginLeft: 2, marginRight: 8 }}>
          <b>{params.value.split("-")[0]}</b>
        </span>
      </Box>
    );
  }

  function getIsActiveIcon(params) {
    const CheckIcon = () => (
      <Box sx={{ display: 'flex', alignItems: 'center', width: "100%", justifyContent: "center" }}>
        <CheckOutlinedIcon color="success" />
      </Box>
    );
    const CrossIcon = () => (
      <Box sx={{ display: 'flex', alignItems: 'center', width: "100%", justifyContent: "center" }}>
        <CloseIcon color="error" />
      </Box>
    );
    if (Object.keys(params.row).length > 0) {
      return (
        params.row.isActive ? <CheckIcon /> : <CrossIcon />
      );
    }
    return null;
  }

  function getIsDeletedIcon(params) {
    const DeletedLabel = () => (
      <Box sx={{ display: 'flex', alignItems: 'center', width: "100%", justifyContent: "center" }}>
        <span style={{ color: "red" }} >Deleted</span>
      </Box>
    );

    return (
      params.row.isDeleted ? <DeletedLabel /> : null
    );
  }

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  };

  //-------------------- funciones de pestañas de cuentas --------------------

  function intManageAccount(id, assignedUserIsDeleted) {
    if (assignedUserIsDeleted === "true") {
      showNotification("User is deleted", 'error'); // Mostrar notificación de error
    } else {
      setUserIdOnEditing(id);
    }
  }

  function onPaginationChangeHandler(model) {
    const { page, pageSize: newPageSize } = model;
    if (newPageSize !== pageSize) setPageSize(newPageSize);
    let pageGridFixed = page + 1;
    if (pageGridFixed !== pageNumber) {
      setPageNumber(pageGridFixed);
    }
  }

  useEffect(() => {
    searchParams.set("pageNumber", pageNumber);
    searchParams.set("pageSize", pageSize);
    if (emailStatusFilter){
      searchParams.set("emailStatusFilter", emailStatusFilter);
    }
    searchParams.set("search", search);
    history.push(`/dashboard/admintools?${searchParams.toString()}`);
  }, [pageNumber, pageSize, search, emailStatusFilter]);

  return (
    <div style={{ height: '70vh', width: '100%' }}>
      <div className='tab-title-container'>
        <h3 style={{ margin: "0" }}>Buying Emails</h3>
      </div>

      <EditUser
        userId={userIdOnEditing}
        modalIsOpen={userIdOnEditing}
        closeModal={() => setUserIdOnEditing(null)}
      />

      <Modal
        open={Boolean(openAddEmailModal)}
        onClose={() => setOpenAddEmailModal(null)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <BuyingEmailForm
            title={openAddEmailModal?.title}
            userId={openAddEmailModal?.userId}
            emailId={openAddEmailModal?.emailId}
            onSave={openAddEmailModal?.onSave}
            onClose={() => setOpenAddEmailModal(null)}
            onSuccess={onEmailFormSuccess}
            onError={onEmailFormError}
          />
        </Box>
      </Modal>

      {/* Caja de búsqueda y filtros */}
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', width: '100%', marginBottom: '0.75em' }}>
        {/* Caja de búsqueda */}
        <div style={{ width: '25%', margin: '0 0.5em 0 0' }}>
          <DebounceInput
            sx={{ width: '100%' }}
            defaultValue={searchDflt}
            placeholder="Search..."
            debounceTimeout={500}
            handleDebounce={(value) => setSearch(value)}
          />
        </div>

        {/* Filtro de estado del email */}
        <div>          
          <Select
            sx={{ height: '41px', width: '400px' }}
            value={emailStatusFilter}
            onChange={(e) => setEmailStatusFilter(e.target.value)}
          >
            <MenuItem key={"ACTIVE_ONLY"} value={"ACTIVE_ONLY"}>Only show Active Buying Emails</MenuItem>
            <MenuItem key={"INACTIVE_ONLY"} value={"INACTIVE_ONLY"}>Only show Inactive Buying Emails</MenuItem>
            <MenuItem key={"ALL"} value={"ALL"}>Show Both Active and Inactive Buying Emails</MenuItem>
          </Select>
        </div>
      </div>

      {/* DataGrid */}
      <DataGridPremium 
        columns={columns} 
        rows={buyingEmailAsArray} 
        apiRef={apiRef}
        sx={{ borderRadius: "1em", backgroundColor: 'white', padding: '1em', '& .MuiBadge-badge': { display: 'none' } }}
        pagination
        paginationMode="server"
        rowCount={buyingEmailsRes?.totalCount || 0}
        loading={isBuyingEmailsLoading || isBuyingEmailFetching}
        disableRowSelectionOnClick
        onPaginationModelChange={onPaginationChangeHandler}
        page={pageNumber - 1} // La paginación comienza en 0, pero la API comienza en 1
        initialState={useKeepGroupedColumnsHidden({
          apiRef,
          initialState: {
            pagination: { paginationModel: { pageSize: pageSizeDflt, page: pageNumberDflt - 1 } },
            rowGrouping: {
              model: ['assignedUserName'],
            },
            sorting: {
              sortModel: [{ "field": "__row_group_by_columns_group__", "sort": "asc" }, { "field": "emailAddress", "sort": "asc" }],
            },
            columns: DEFAULT_HIDE_COLUMNS
          }
        })}
      />
    </div>
  );
}